import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import { Header } from '../components/Header';
import { StatusBadge, ViolationTypeBadge } from '../components/StatusBadge';
import { MediaThumbnails, MediaViewer } from '../components/ViolationCard';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { officerService, violationService } from '../services/api';
import {
    MapPin,
    FileText,
    Clock,
    CheckCircle2,
    XCircle,
    Loader2,
    User,
    Calendar,
    ScanLine,
    AlertCircle,
    Filter,
    ChevronLeft,
    ChevronRight,
    Lock,
    History
} from 'lucide-react';
import { format } from 'date-fns';

export default function OfficerDashboard() {
    const { user, token } = useAuth();
    const [violations, setViolations] = useState([]);
    const [violationTypes, setViolationTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedViolation, setSelectedViolation] = useState(null);
    const [reviewing, setReviewing] = useState(false);
    const [showMediaViewer, setShowMediaViewer] = useState(false);
    
    // Filter state
    const [statusFilter, setStatusFilter] = useState('pending');
    const [typeFilter, setTypeFilter] = useState('all');
    
    // Review form
    const [remarks, setRemarks] = useState('');
    const [confirmedVehicleNumber, setConfirmedVehicleNumber] = useState('');

    // Concurrency lock for the open violation: null | { is_mine, locked_by_name }
    const [selectedLock, setSelectedLock] = useState(null);
    // Approved-violation history for the open violation's vehicle
    const [vehicleHistory, setVehicleHistory] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    // Reporter credibility for the open violation
    const [reputation, setReputation] = useState(null);

    const lockedByOther = !!selectedLock && !selectedLock.is_mine;

    // Id of the violation this officer currently holds a lock on (for unload/idle release)
    const lockedIdRef = useRef(null);

    // Auto-logout after 5 min idle; release any held lock on the way out.
    useInactivityLogout({
        onTimeout: () => {
            const id = lockedIdRef.current;
            if (id) {
                officerService.unlockKeepalive(id);
                lockedIdRef.current = null;
            }
        },
    });

    const fetchViolations = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== 'all') params.status = statusFilter;
            if (typeFilter !== 'all') params.violation_type = typeFilter;
            const response = await officerService.list(params);
            setViolations(response.data);
        } catch (error) {
            toast.error('Failed to fetch violations');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, typeFilter]);

    const fetchViolationTypes = useCallback(async () => {
        try {
            const response = await violationService.getTypes();
            setViolationTypes(response.data);
        } catch (error) {
            console.error('Failed to fetch violation types');
        }
    }, []);

    useEffect(() => {
        fetchViolations();
        fetchViolationTypes();
    }, [fetchViolations, fetchViolationTypes]);

    // ── Review lock lifecycle ──────────────────────────────────────────────────
    // Acquire a lock when a pending violation is opened, heartbeat it every 60s so
    // it doesn't expire while actively reviewing, and release it on close/switch.
    useEffect(() => {
        const v = selectedViolation;
        if (!v || !['pending', 'appealed'].includes(v.status)) {
            setSelectedLock(null);
            return;
        }
        let active = true;
        const id = v.id;

        const acquire = async () => {
            try {
                const res = await officerService.lock(id);
                if (!active) return;
                if (res.data.acquired) {
                    setSelectedLock({ is_mine: true });
                    lockedIdRef.current = id;
                } else {
                    setSelectedLock(res.data.lock || null);
                    lockedIdRef.current = null;
                }
            } catch {
                /* lock is best-effort UX; ignore transient failures */
            }
        };

        acquire();
        const heartbeat = setInterval(acquire, 60000);
        return () => {
            active = false;
            clearInterval(heartbeat);
            officerService.unlock(id).catch(() => {});
            if (lockedIdRef.current === id) lockedIdRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedViolation?.id, selectedViolation?.status]);

    // ── Release the lock on page unload / refresh ──────────────────────────────
    // React effect cleanup does NOT run on a hard refresh or tab close, so a
    // keepalive fetch is used to release any lock this officer is holding.
    useEffect(() => {
        const release = () => {
            const id = lockedIdRef.current;
            if (id) officerService.unlockKeepalive(id);
        };
        window.addEventListener('pagehide', release);
        return () => window.removeEventListener('pagehide', release);
    }, []);

    // ── Vehicle approved-history ───────────────────────────────────────────────
    useEffect(() => {
        const v = selectedViolation;
        if (!v) {
            setVehicleHistory(null);
            return;
        }
        let active = true;
        setHistoryLoading(true);
        setReputation(null);
        officerService
            .vehicleHistory({ vehicle_number: v.vehicle_number, exclude_id: v.id, violation_type: v.violation_type })
            .then((res) => { if (active) setVehicleHistory(res.data); })
            .catch(() => { if (active) setVehicleHistory(null); })
            .finally(() => { if (active) setHistoryLoading(false); });
        officerService
            .reporterReputation(v.user_id)
            .then((res) => { if (active) setReputation(res.data); })
            .catch(() => { if (active) setReputation(null); });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedViolation?.id]);

    const handleReview = async (decision, { remarksOverride, confirmedOverride } = {}) => {
        if (!selectedViolation) return;
        const confirmed = confirmedOverride || confirmedVehicleNumber;
        if (!confirmed) {
            toast.error('Please select the correct vehicle number before reviewing');
            return;
        }

        setReviewing(true);
        try {
            await officerService.review(selectedViolation.id, {
                decision,
                remarks: remarksOverride ?? remarks,
                confirmed_vehicle_number: confirmed,
            });

            toast.success(`Violation ${decision}!`);
            setSelectedViolation(null);
            setRemarks('');
            setConfirmedVehicleNumber('');
            setSelectedLock(null);
            setVehicleHistory(null);
            fetchViolations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Review failed');
        } finally {
            setReviewing(false);
        }
    };

    // Quick-reject when an approved similar violation already exists in the window.
    const handleRejectDuplicate = () => {
        if (!selectedViolation) return;
        const reason = `A similar violation is approved in last ${vehicleHistory?.window_hours ?? 48} hours`;
        setRemarks(reason);
        handleReview('rejected', {
            remarksOverride: reason,
            confirmedOverride: confirmedVehicleNumber || selectedViolation.vehicle_number,
        });
    };

    const stats = {
        pending: violations.filter(v => v.status === 'pending').length,
        approved: violations.filter(v => v.status === 'approved').length,
        rejected: violations.filter(v => v.status === 'rejected').length
    };

    const currentIndex = selectedViolation 
        ? violations.findIndex(v => v.id === selectedViolation.id)
        : -1;

    const goToNext = () => {
        if (currentIndex < violations.length - 1) {
            setSelectedViolation(violations[currentIndex + 1]);
            setRemarks('');
            setConfirmedVehicleNumber('');
        }
    };

    const goToPrev = () => {
        if (currentIndex > 0) {
            setSelectedViolation(violations[currentIndex - 1]);
            setRemarks('');
            setConfirmedVehicleNumber('');
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Officer Dashboard</h1>
                        <p className="text-muted-foreground mt-1">
                            Review and process traffic violation reports
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Logged in as:</span>
                        <span className="font-medium">{user?.name}</span>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                    <Card className="border-warning/50">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-warning" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.pending}</p>
                                    <p className="text-xs text-muted-foreground">Pending Review</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-success/50">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                                    <CheckCircle2 className="w-5 h-5 text-success" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.approved}</p>
                                    <p className="text-xs text-muted-foreground">Approved</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-destructive/50">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                                    <XCircle className="w-5 h-5 text-destructive" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.rejected}</p>
                                    <p className="text-xs text-muted-foreground">Rejected</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content */}
                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Left - Queue */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Violation Queue</CardTitle>
                                    <CardDescription>
                                        Select a violation to review
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Filter className="w-4 h-4 text-muted-foreground" />
                                </div>
                            </div>
                            
                            {/* Filters */}
                            <div className="flex gap-2 mt-4">
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-[140px]" data-testid="status-filter">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Status</SelectItem>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="appealed">Appealed</SelectItem>
                                        <SelectItem value="approved">Approved</SelectItem>
                                        <SelectItem value="rejected">Rejected</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={typeFilter} onValueChange={setTypeFilter}>
                                    <SelectTrigger className="w-[160px]" data-testid="type-filter">
                                        <SelectValue placeholder="Violation Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Types</SelectItem>
                                        {violationTypes.map(type => (
                                            <SelectItem key={type.id} value={type.id}>
                                                {type.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : violations.length === 0 ? (
                                <div className="text-center py-12">
                                    <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                                    <p className="text-muted-foreground">No violations found</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Try adjusting your filters
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                                    {violations.map((violation) => (
                                        <button
                                            key={violation.id}
                                            onClick={() => {
                                                setSelectedViolation(violation);
                                                setRemarks('');
                                                setConfirmedVehicleNumber('');
                                            }}
                                            className={`w-full text-left p-4 rounded-lg border transition-colors ${
                                                selectedViolation?.id === violation.id
                                                    ? 'border-primary bg-primary/5'
                                                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                                            }`}
                                            data-testid={`queue-item-${violation.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-mono font-bold">
                                                            {violation.vehicle_number}
                                                        </span>
                                                        {violation.plate_match_status === 'Match' && (
                                                            <ScanLine className="w-3 h-3 text-success" />
                                                        )}
                                                        {violation.plate_match_status === 'Mismatch' && (
                                                            <span
                                                                className="text-[10px] uppercase tracking-wide text-destructive bg-destructive/10 border border-destructive/30 px-1.5 py-0.5 rounded flex items-center gap-1"
                                                                data-testid={`queue-mismatch-${violation.id}`}
                                                            >
                                                                <AlertCircle className="w-2.5 h-2.5" />
                                                                Mismatch
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <ViolationTypeBadge type={violation.violation_type} />
                                                        <StatusBadge status={violation.status} size="sm" />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1.5 whitespace-nowrap">
                                                    {violation.lock && !violation.lock.is_mine && (
                                                        <span
                                                            title={`Being reviewed by ${violation.lock.locked_by_name || 'another officer'}`}
                                                            className="text-warning"
                                                            data-testid={`queue-lock-${violation.id}`}
                                                        >
                                                            <Lock className="w-3.5 h-3.5" />
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-muted-foreground">
                                                        {format(new Date(violation.created_at), 'MMM d')}
                                                    </span>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Right - Review Panel */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle>Review Panel</CardTitle>
                                    <CardDescription>
                                        {selectedViolation 
                                            ? `Reviewing ${selectedViolation.vehicle_number}`
                                            : 'Select a violation to review'
                                        }
                                    </CardDescription>
                                </div>
                                {selectedViolation && (
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={goToPrev}
                                            disabled={currentIndex <= 0}
                                            data-testid="prev-violation"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </Button>
                                        <span className="text-sm text-muted-foreground">
                                            {currentIndex + 1} / {violations.length}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={goToNext}
                                            disabled={currentIndex >= violations.length - 1}
                                            data-testid="next-violation"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {!selectedViolation ? (
                                <div className="text-center py-12">
                                    <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                                    <p className="text-muted-foreground">
                                        Select a violation from the queue to review
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Concurrency lock warning */}
                                    {lockedByOther && (
                                        <div
                                            className="border border-warning/40 bg-warning/10 rounded-lg p-3 flex items-start gap-2"
                                            data-testid="lock-warning"
                                        >
                                            <Lock className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                                            <div className="text-sm">
                                                <p className="font-semibold text-warning">Currently being reviewed</p>
                                                <p className="text-muted-foreground">
                                                    {selectedLock?.locked_by_name || 'Another officer'} has this violation open.
                                                    Review actions are disabled to avoid a conflict.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Appeal notice */}
                                    {selectedViolation.status === 'appealed' && selectedViolation.appeal && (
                                        <div className="border border-info/40 bg-info/10 rounded-lg p-3" data-testid="appeal-notice">
                                            <p className="font-semibold text-info text-sm">Appeal submitted — re-review requested</p>
                                            <p className="text-sm text-muted-foreground mt-1">"{selectedViolation.appeal.reason}"</p>
                                        </div>
                                    )}

                                    {/* Media Preview */}
                                    <div>
                                        <Label className="mb-2 block">Evidence</Label>
                                        <MediaThumbnails
                                            mediaFiles={selectedViolation.media_files}
                                            token={token}
                                            onView={() => setShowMediaViewer(true)}
                                        />
                                    </div>

                                    {/* Violation Details */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-muted-foreground">Reported Vehicle No.</Label>
                                            <p className="font-mono font-bold text-lg">
                                                {selectedViolation.vehicle_number}
                                            </p>
                                            {selectedViolation.ocr_detected_plate && (
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    OCR detected:{' '}
                                                    <span className={`font-mono font-semibold ${selectedViolation.plate_match_status === 'Mismatch' ? 'text-destructive' : 'text-success'}`}>
                                                        {selectedViolation.ocr_detected_plate}
                                                    </span>
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <Label className="text-muted-foreground">OCR Status</Label>
                                            {selectedViolation.plate_match_status ? (
                                                <span
                                                    className={`inline-flex items-center gap-1 text-sm font-semibold px-2 py-0.5 rounded ${
                                                        selectedViolation.plate_match_status === 'Match'
                                                            ? 'text-success bg-success/10'
                                                            : 'text-destructive bg-destructive/10 border border-destructive/30'
                                                    }`}
                                                    data-testid="ocr-status-badge"
                                                >
                                                    {selectedViolation.plate_match_status === 'Match'
                                                        ? <ScanLine className="w-3 h-3" />
                                                        : <AlertCircle className="w-3 h-3" />
                                                    }
                                                    {selectedViolation.plate_match_status}
                                                </span>
                                            ) : (
                                                <p className="text-sm text-muted-foreground">No OCR data</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-muted-foreground">Violation Type</Label>
                                            <p><ViolationTypeBadge type={selectedViolation.violation_type} /></p>
                                        </div>
                                    </div>

                                    {selectedViolation.plate_match_status === 'Mismatch' && (
                                        <div className="border border-destructive/30 bg-destructive/5 rounded-lg p-3 flex items-start gap-2" data-testid="ocr-mismatch-alert">
                                            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                                            <div className="text-sm">
                                                <p className="font-semibold text-destructive">License plate mismatch detected</p>
                                                <p className="text-muted-foreground">
                                                    The plate read from the image ({selectedViolation.ocr_detected_plate}) does not match
                                                    what the reporter entered ({selectedViolation.vehicle_number}). Select the correct plate below before reviewing.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-muted-foreground flex items-center gap-1">
                                                <MapPin className="w-3 h-3" /> Location
                                            </Label>
                                            <p className="text-sm">{selectedViolation.location || 'Not specified'}</p>
                                        </div>
                                        <div>
                                            <Label className="text-muted-foreground flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> Occurred
                                            </Label>
                                            <p className="text-sm">
                                                {selectedViolation.violation_datetime
                                                    ? format(new Date(selectedViolation.violation_datetime), 'PPp')
                                                    : '—'}
                                            </p>
                                        </div>
                                        <div>
                                            <Label className="text-muted-foreground flex items-center gap-1">
                                                <Calendar className="w-3 h-3" /> Reported
                                            </Label>
                                            <p className="text-sm">
                                                {format(new Date(selectedViolation.created_at), 'PPp')}
                                            </p>
                                        </div>
                                    </div>

                                    {selectedViolation.description && (
                                        <div>
                                            <Label className="text-muted-foreground">Description</Label>
                                            <p className="text-sm">{selectedViolation.description}</p>
                                        </div>
                                    )}

                                    <div>
                                        <Label className="text-muted-foreground flex items-center gap-1">
                                            <User className="w-3 h-3" /> Reported By
                                        </Label>
                                        <p className="text-sm">
                                            {selectedViolation.user_name} ({selectedViolation.user_email})
                                        </p>
                                        {reputation && (
                                            <span
                                                className={`inline-flex items-center gap-1 mt-1 text-xs px-2 py-0.5 rounded-full ${
                                                    reputation.low_trust
                                                        ? 'bg-destructive/10 text-destructive border border-destructive/30'
                                                        : 'bg-muted text-muted-foreground'
                                                }`}
                                                data-testid="reporter-reputation"
                                            >
                                                {reputation.low_trust && <AlertCircle className="w-3 h-3" />}
                                                {reputation.total} report{reputation.total === 1 ? '' : 's'}
                                                {reputation.approval_rate != null
                                                    ? ` · ${Math.round(reputation.approval_rate * 100)}% approved`
                                                    : ' · no decisions yet'}
                                                {reputation.low_trust && ' · low trust'}
                                            </span>
                                        )}
                                    </div>

                                    {/* Approved history for this vehicle */}
                                    <div className="pt-4 border-t" data-testid="vehicle-history">
                                        <Label className="text-muted-foreground flex items-center gap-1 mb-2">
                                            <History className="w-3.5 h-3.5" /> Approved history for{' '}
                                            <span className="font-mono">{selectedViolation.vehicle_number}</span>
                                        </Label>

                                        {historyLoading ? (
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                                                <Loader2 className="w-4 h-4 animate-spin" /> Checking records…
                                            </div>
                                        ) : (
                                            <>
                                                {vehicleHistory?.recent_similar?.length > 0 && (
                                                    <div
                                                        className="border border-destructive/30 bg-destructive/5 rounded-lg p-3 mb-3"
                                                        data-testid="similar-approved-alert"
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                                                            <div className="text-sm">
                                                                <p className="font-semibold text-destructive">
                                                                    Possible duplicate
                                                                </p>
                                                                <p className="text-muted-foreground">
                                                                    {vehicleHistory.recent_similar.length} approved same-type
                                                                    violation(s) for this vehicle in the last{' '}
                                                                    {vehicleHistory.window_hours}h.
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {['pending', 'appealed'].includes(selectedViolation.status) && (
                                                            <Button
                                                                variant="destructive"
                                                                size="sm"
                                                                className="mt-2"
                                                                onClick={handleRejectDuplicate}
                                                                disabled={reviewing || lockedByOther}
                                                                data-testid="reject-duplicate-btn"
                                                            >
                                                                Reject — similar approved in {vehicleHistory.window_hours}h
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}

                                                {vehicleHistory?.approved?.length > 0 ? (
                                                    <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                                                        {vehicleHistory.approved.map((h) => (
                                                            <div
                                                                key={h.id}
                                                                className="flex items-center justify-between gap-2 rounded-md border p-2"
                                                            >
                                                                <div className="min-w-0">
                                                                    <ViolationTypeBadge type={h.violation_type} />
                                                                    <p className="text-xs text-muted-foreground mt-1 truncate">
                                                                        {h.location || 'No location'}
                                                                    </p>
                                                                </div>
                                                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                                    {format(new Date(h.violation_datetime || h.created_at), 'MMM d, yyyy')}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">
                                                        No prior approved violations for this vehicle.
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    {/* Review Actions */}
                                    {['pending', 'appealed'].includes(selectedViolation.status) ? (
                                        <div className="space-y-4 pt-4 border-t">
                                            {/* Mandatory vehicle number confirmation */}
                                            <div className="space-y-2">
                                                <Label>
                                                    Confirm Vehicle Number{' '}
                                                    <span className="text-destructive">*</span>
                                                </Label>
                                                <Select
                                                    value={confirmedVehicleNumber}
                                                    onValueChange={setConfirmedVehicleNumber}
                                                    data-testid="confirm-plate-select"
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select the correct vehicle number" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value={selectedViolation.vehicle_number}>
                                                            {selectedViolation.vehicle_number}{' '}
                                                            <span className="text-muted-foreground">(Reported by user)</span>
                                                        </SelectItem>
                                                        {selectedViolation.ocr_detected_plate &&
                                                            selectedViolation.ocr_detected_plate !== selectedViolation.vehicle_number && (
                                                            <SelectItem value={selectedViolation.ocr_detected_plate}>
                                                                {selectedViolation.ocr_detected_plate}{' '}
                                                                <span className="text-muted-foreground">(Detected by OCR)</span>
                                                            </SelectItem>
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                                {!confirmedVehicleNumber && (
                                                    <p className="text-xs text-muted-foreground">
                                                        You must confirm the vehicle number before approving or rejecting.
                                                    </p>
                                                )}
                                            </div>

                                            <div>
                                                <Label htmlFor="remarks">Remarks (Optional)</Label>
                                                <Textarea
                                                    id="remarks"
                                                    placeholder="Add review remarks..."
                                                    value={remarks}
                                                    onChange={(e) => setRemarks(e.target.value)}
                                                    rows={2}
                                                    data-testid="review-remarks-input"
                                                />
                                            </div>
                                            <div className="flex gap-3">
                                                <Button
                                                    className="flex-1 bg-success hover:bg-success/90"
                                                    onClick={() => handleReview('approved')}
                                                    disabled={reviewing || !confirmedVehicleNumber || lockedByOther}
                                                    data-testid="approve-btn"
                                                >
                                                    {reviewing ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <CheckCircle2 className="w-4 h-4 mr-2" />
                                                            Approve
                                                        </>
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    className="flex-1"
                                                    onClick={() => handleReview('rejected')}
                                                    disabled={reviewing || !confirmedVehicleNumber || lockedByOther}
                                                    data-testid="reject-btn"
                                                >
                                                    {reviewing ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <XCircle className="w-4 h-4 mr-2" />
                                                            Reject
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-4 rounded-lg bg-muted/50 border">
                                            <p className="text-sm font-medium mb-2">Already Reviewed</p>
                                            <div className="space-y-1 text-sm">
                                                <p>
                                                    <span className="text-muted-foreground">Decision:</span>{' '}
                                                    <StatusBadge status={selectedViolation.status} size="sm" />
                                                </p>
                                                {selectedViolation.reviewer_name && (
                                                    <p>
                                                        <span className="text-muted-foreground">By:</span>{' '}
                                                        {selectedViolation.reviewer_name}
                                                    </p>
                                                )}
                                                {selectedViolation.reviewed_at && (
                                                    <p>
                                                        <span className="text-muted-foreground">On:</span>{' '}
                                                        {format(new Date(selectedViolation.reviewed_at), 'PPp')}
                                                    </p>
                                                )}
                                                {selectedViolation.confirmed_vehicle_number && (
                                                    <p>
                                                        <span className="text-muted-foreground">Confirmed plate:</span>{' '}
                                                        <span className="font-mono font-semibold">{selectedViolation.confirmed_vehicle_number}</span>
                                                    </p>
                                                )}
                                                {selectedViolation.api_response?.reference_id && (
                                                    <p>
                                                        <span className="text-muted-foreground">Challan ref:</span>{' '}
                                                        <span className="font-mono font-semibold text-success">{selectedViolation.api_response.reference_id}</span>
                                                    </p>
                                                )}
                                                {selectedViolation.review_remarks && (
                                                    <p>
                                                        <span className="text-muted-foreground">Remarks:</span>{' '}
                                                        {selectedViolation.review_remarks}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>

            {/* Media Viewer */}
            {showMediaViewer && selectedViolation && (
                <MediaViewer
                    mediaFiles={selectedViolation.media_files}
                    token={token}
                    onClose={() => setShowMediaViewer(false)}
                />
            )}
        </div>
    );
}
